import { gql } from '@apollo/client';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isEmpty, isNil } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Shield } from '~auth/Shield';
import { useAuth } from '~auth/useAuth';
import { updateCustomerHandleInStorage } from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { CustomerSubscriptionsTableSubscriptionFragment } from '~components/organization/CustomerSubscriptionsTable/__generated__/CustomerSubscriptionsTable.graphql';
import { CustomerSubscriptions } from '~components/organization/CustomerSubscriptionsV2/CustomerSubscriptions';
import { CustomerUsersTable } from '~components/organization/CustomerUsersTable';
import { CustomerUsersTableUserFragment } from '~components/organization/CustomerUsersTable/__generated__/CustomerUsersTable.graphql';
import { useCustomerUsersTable } from '~components/organization/CustomerUsersTable/useCustomerUsersTable';
import {
  customerBaseValidationSchema,
  customerErrorMap,
  toHandle,
} from '~components/organization/utils';
import { AvatarInput, FileUploadProps } from '~components/ui/AvatarInput';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import useGoBack from '~components/useGoBack';
import { NotificationStatus, WaveSubscriptionStatus } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { getId } from '~utils/data';
import { fromError } from '~utils/errors';
import { useFeatureFlag } from '~utils/features';
import { useSubscriptionType } from '~utils/subscriptions';
import { ensure, isDefined } from '~utils/types';
import { useS3Upload } from '~utils/useS3Upload';
import { CustomerPageCustomerFragment, useCustomerPageQuery } from '../__generated__/[id].graphql';
import { useUpdateNotificationStatusMutation } from '../__generated__/emailNotification.graphql';
import { useEditCustomerNewMutation } from './__generated__/[id].graphql';

const schema = z.object({
  id: z.string().uuid(),
  file: z.instanceof(File).optional(),
  uploadUrl: z.string().optional(),
  userIds: z.array(z.string()),
  subscriptionIds: z.array(z.string()),
  ...customerBaseValidationSchema,
});

type FormValues = z.infer<typeof schema>;

export function OrganizationCustomerEditPage() {
  const { customerId } = useParams();
  const toast = useToast();
  const [editCustomerNew] = useEditCustomerNewMutation();
  const { upload } = useS3Upload();
  const analytics = useAnalyticsReporter();
  const handleGoBack = useGoBack();
  const { verifyUserPermissions, user } = useAuth();

  const { data, loading, error, refetch } = useCustomerPageQuery({
    variables: {
      customerId: ensure(customerId),
    },
  });

  const updateForm = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userIds: data?.customer?.users.map(getId).sort() ?? [],
      subscriptionIds: data?.customer?.waveSubscriptions?.map(getId).sort() ?? [],
    },
  });

  const { isSubmitting, isDirty, isValid } = updateForm.formState;

  const canSubmit = !isSubmitting && isValid && isDirty;

  const handleUpdateCustomer = async ({
    id,
    name,
    handle,
    avatarUrl,
    file,
    uploadUrl,
    userIds,
    subscriptionIds,
  }: FormValues) => {
    if (!isNil(uploadUrl) && !isNil(file)) {
      try {
        await upload(uploadUrl, file);
      } catch (error) {
        toast({
          status: 'error',
          title: t('uploadImageErr'),
          description: fromError(error, 'UploadFile'),
        });
        return;
      }
    }

    try {
      const { data } = await editCustomerNew({
        variables: {
          input: {
            customerId: id,
            handle,
            name,
            subscriptionIds,
            avatarUrl: avatarUrl ?? null,
            ...(verifyUserPermissions([Permission.FeatureCustomerScopedAccess]) && { userIds }),
          },
        },
      });

      if (!isDefined(data)) {
        throw new Error('Update customer failed');
      }

      if (user?.subId && id && handle) {
        await updateCustomerHandleInStorage(user.subId, id, handle);
      }

      analytics.track('customerUpdateComplete');

      toast({
        status: 'success',
        title: t('customerSettingsUpdated'),
      });
      refetch();
    } catch (error) {
      toast({
        status: 'error',
        title: t('editCustomerErr'),
        description: fromError(error, 'EditCustomer', customerErrorMap),
      });
    }
  };

  const confirmSubscriptionUnlink = useDestructiveAction<{
    addedSubscriptions: string[];
    removedSubscriptions: string[];
  }>({
    title: t('subscriptionConfirmation'),
    message: ({ addedSubscriptions, removedSubscriptions }) => {
      const allSubs = data?.organization.waveSubscriptions ?? [];

      const getNames = (ids: string[]) =>
        allSubs.filter(({ id }) => ids.includes(id)).map((s) => s.userGivenName ?? s.name);

      const addedNames = getNames(addedSubscriptions);
      const removedNames = getNames(removedSubscriptions);

      const customerName = data?.customer?.name;
      let key = '';
      const values: Record<string, string | number | undefined> = { customerName };
      if (removedNames.length === 1 && addedNames.length === 0) {
        key = 'removeSingleSubscriptionConfirmation';
        values.subscriptionName = removedNames[0];
      } else if (removedNames.length > 1 && addedNames.length === 0) {
        key = 'removeMultipleSubscriptionConfirmation';
        values.count = removedNames.length;
      }

      return <Trans i18nKey={key} values={values} components={{ strong: <strong /> }} />;
    },
    variant: 'danger',
    confirmLabel: t('confirm'),
    onConfirm: () => updateForm.handleSubmit(handleUpdateCustomer)(),
  });

  const confirmSubscription = useDestructiveAction<{
    addedSubscriptions: string[];
    removedSubscriptions: string[];
  }>({
    title: t('subscriptionConfirmation'),
    message: ({ addedSubscriptions, removedSubscriptions }) => {
      const allSubs = data?.organization.waveSubscriptions ?? [];

      const getNames = (ids: string[]) =>
        allSubs.filter(({ id }) => ids.includes(id)).map((s) => s.userGivenName ?? s.name);

      const addedNames = getNames(addedSubscriptions);
      const removedNames = getNames(removedSubscriptions);

      const customerName = data?.customer?.name;

      let key = '';
      const values: Record<string, string | number | undefined> = { customerName };

      // --- SINGLE CASES ---
      if (addedNames.length === 1 && removedNames.length === 0) {
        key = 'assignSingleSubscriptionConfirmation';
        values.subscriptionName = addedNames[0];
      } else if (removedNames.length === 1 && addedNames.length === 0) {
        key = 'removeSingleSubscriptionConfirmation';
        values.subscriptionName = removedNames[0];
      }

      // --- MULTIPLE CASES ---
      else if (addedNames.length > 1 && removedNames.length === 0) {
        key = 'assignMultipleSubscriptionConfirmation';
        values.count = addedNames.length;
      } else if (removedNames.length > 1 && addedNames.length === 0) {
        key = 'removeMultipleSubscriptionConfirmation';
        values.count = removedNames.length;
      }

      // --- MIXED CASES ---
      else if (addedNames.length === 1 && removedNames.length === 1) {
        key = 'mixedSingleSubscriptionConfirmation';
        values.subscriptionName = addedNames[0];
        values.removeSubscriptionName = removedNames[0];
      } else if (addedNames.length > 1 && removedNames.length > 1) {
        key = 'mixedMultipleSubscriptionConfirmation';
        values.assignCount = addedNames.length;
        values.removeCount = removedNames.length;
      } else if (addedNames.length > 1 && removedNames.length === 1) {
        key = 'mixedAssignMultipleSubscriptionConfirmation';
        values.assignCount = addedNames.length;
        values.removeSubscriptionName = removedNames[0];
      } else if (addedNames.length === 1 && removedNames.length > 1) {
        key = 'mixedRemoveMultipleSubscriptionConfirmation';
        values.subscriptionName = addedNames[0];
        values.removeCount = removedNames.length;
      }

      return <Trans i18nKey={key} values={values} components={{ strong: <strong /> }} />;
    },
    variant: 'warning',
    confirmLabel: t('confirm'),
    onConfirm: () => updateForm.handleSubmit(handleUpdateCustomer)(),
  });

  const confirmOrUpdateCustomer = async (values: FormValues) => {
    const currentIds = data?.customer?.waveSubscriptions?.map(getId) ?? [];
    const allSubs = data?.organization.waveSubscriptions ?? [];

    const nextIds = values.subscriptionIds.filter((id) =>
      allSubs.some((s) => s.id === id && s.status === WaveSubscriptionStatus.Active),
    );

    updateForm.setValue('subscriptionIds', nextIds, { shouldDirty: true });

    const currentSet = new Set(currentIds);
    const nextSet = new Set(nextIds);

    const removedIds = currentIds.filter((id) => !nextSet.has(id));
    const addedIds = nextIds.filter((id) => !currentSet.has(id));

    if (addedIds.length === 0 && removedIds.length > 0) {
      await confirmSubscriptionUnlink.askConfirmation({
        addedSubscriptions: addedIds,
        removedSubscriptions: removedIds,
      });
      return;
    }

    if (addedIds.length > 0 || removedIds.length > 0) {
      await confirmSubscription.askConfirmation({
        addedSubscriptions: addedIds,
        removedSubscriptions: removedIds,
      });
      return;
    }

    await handleUpdateCustomer({ ...values, subscriptionIds: nextIds });
  };

  useEffect(() => {
    if (loading === false && data?.customer) {
      updateForm.reset({
        id: data.customer.id,
        name: data.customer.name,
        handle: data.customer.handle,
        avatarUrl: data.customer.avatarUrl ?? undefined,
        userIds: data.customer.users.map(getId).sort() ?? [],
        subscriptionIds: data.customer.waveSubscriptions?.map(getId).sort() ?? [],
      });
    }
  }, [data?.customer, updateForm, loading]);

  useEffect(() => {
    analytics.track('customerUpdateStart');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VerticalTabContent
      title={t('editCustomer')}
      isLoading={loading}
      error={error}
      onGoBack={handleGoBack}
      data={data?.customer}
      hasStickyHeader={true}
      action={{
        label: t('applyChanges'),
        disabled: !canSubmit,
        isLoading: isSubmitting,
        onClick: () => confirmOrUpdateCustomer(updateForm.getValues()),
      }}
    >
      {(customer) => (
        <>
          <EditCustomerForm
            customer={customer}
            users={data?.organization.users ?? []}
            subscriptions={data?.organization.waveSubscriptions ?? []}
            form={updateForm}
          />
          {confirmSubscription.confirmationNode}
          {confirmSubscriptionUnlink.confirmationNode}
        </>
      )}
    </VerticalTabContent>
  );
}

type EditCustomerFormProps = {
  customer: CustomerPageCustomerFragment;
  users: CustomerUsersTableUserFragment[];
  subscriptions: CustomerSubscriptionsTableSubscriptionFragment[];
  form: UseFormReturn<FormValues>;
};

function EditCustomerForm({ customer, users, subscriptions, form }: EditCustomerFormProps) {
  const [currentImage, setCurrentImage] = useState(customer.avatarUrl);
  const navigate = useNavigate();

  const { hasWaveSubscription: hasEssentialSubscription } = useSubscriptionType(customer);

  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');

  const customerUserIds = customer.users.map(getId);

  // Stable reference to original subscription ids — computed once on mount
  // so dirty-checking compares against the server value, not a re-derived array.
  const originalSubscriptionIds = useMemo(
    () => customer.waveSubscriptions?.map(getId) ?? [],
    [customer.waveSubscriptions],
  );

  const table = useCustomerUsersTable(users, customerUserIds, isSubscriptionsEnabled);

  const {
    register,
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors, touchedFields, isSubmitSuccessful },
  } = form;

  const { ref: nameInputRef, onChange: onNameChange, ...nameInputProps } = register('name');
  const currentHandle = watch('handle');
  const currentName = watch('name');

  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(
    customer.notification?.status === NotificationStatus.Enabled,
  );

  const handleAvatarInputChange = (uploadData: FileUploadProps) => {
    if (isNil(uploadData.file)) {
      setCurrentImage(undefined);
      setValue('file', undefined, { shouldDirty: true });
    } else {
      setValue('file', uploadData.file, { shouldDirty: true });
    }

    setValue('avatarUrl', uploadData.uploadPayload?.assetUrl);
    setValue('uploadUrl', uploadData.uploadPayload?.uploadUrl);

    clearErrors('avatarUrl');
  };

  const handleAvatarInputError = (error: Error) => {
    setError('avatarUrl', { message: error.message });
  };

  const handleCustomerNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNameChange(event);

    if (!touchedFields.handle) {
      setValue('handle', toHandle(event.target.value));
    }
  };

  const handleSubscriptionIdsChange = (subscriptionIds: string[]) => {
    setValue('subscriptionIds', [...subscriptionIds].sort(), {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const toast = useToast();
  const [updateNotificationStatus] = useUpdateNotificationStatusMutation({
    refetchQueries: ['EmailNotificationsPage'],
    awaitRefetchQueries: true,
  });

  const handleStatusUpdate = useCallback(
    async (status: NotificationStatus) => {
      try {
        const { data: result } = await updateNotificationStatus({
          variables: {
            input: {
              customerId: ensure(customer.id),
              status,
            },
          },
        });

        if (!result?.notificationStatusUpdate) {
          throw new Error('notificationUpdateFailed');
        }
        setEmailNotificationsEnabled(status === NotificationStatus.Enabled);
        const isEnabled = result?.notificationStatusUpdate.status === NotificationStatus.Enabled;
        toast({
          status: isEnabled ? 'success' : 'info',
          title: isEnabled
            ? t('emailNotificationsEnabled', { customerName: customer.name })
            : t('emailNotificationsDisabled', { customerName: customer.name }),
          description: (
            <Text>
              <Trans
                i18nKey={
                  isEnabled ? 'emailNotificationsEnabledDesc' : 'emailNotificationsDisabledDesc'
                }
                values={{ customerName: customer.name }}
                components={{ bold: <Text as="span" fontWeight="bold" /> }}
              />
            </Text>
          ),
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('updateNotificationError'),
          description: fromError(err, 'NotificationSettingsUpdate'),
        });
      }
    },
    [updateNotificationStatus, customer.id, customer.name, toast],
  );

  useEffect(() => {
    const handle = getValues('handle');
    const generatedHandle = toHandle(getValues('name'));

    if (handle !== generatedHandle) {
      setValue('handle', handle, { shouldTouch: true });
    }
  }, [getValues, setValue]);

  useEffect(() => {
    const checkedUserIds = Object.keys(table.state.selectedRowIds).sort();

    setValue('userIds', checkedUserIds, {
      shouldTouch: true,
      shouldDirty: true,
    });
  }, [setValue, table.state.selectedRowIds]);

  useEffect(() => {
    if (currentHandle === customer.handle && currentName === customer.name) {
      reset(undefined, { keepValues: true, keepDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHandle, currentName]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(undefined, { keepValues: true, keepDirty: false, keepDefaultValues: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful, reset]);

  const handleManageNotificationsClick = () => {
    navigate(`./../emailNotification/${customer.id}`, { state: { canGoBack: true } });
  };

  // Pass the original server ids to CustomerSubscriptions so it can
  // determine the current plan badge — not the live form value.
  const customerSubscriptionIds = originalSubscriptionIds;

  return (
    <form>
      <VStack alignItems="flex-start" spacing={10}>
        <FormControl isInvalid={Boolean(errors.avatarUrl)}>
          <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
            {t('customerLogo')}
          </Heading>
          <AvatarInput
            onChange={handleAvatarInputChange}
            onError={handleAvatarInputError}
            avatar={currentImage ?? undefined}
          />
          <FormErrorMessage>{errors.avatarUrl?.message}</FormErrorMessage>
        </FormControl>
        {hasEssentialSubscription && (
          <FormControl>
            <Box display="flex" alignItems="center" mb="6">
              <Heading fontSize="1.5rem">{t('emailNotification')}</Heading>
              <Text
                as="span"
                color="blue.500"
                ml="19%"
                cursor="pointer"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="bold"
                _hover={{ textDecoration: 'underline' }}
                onClick={handleManageNotificationsClick}
                whiteSpace="nowrap"
              >
                {t('manageNotifications')}
              </Text>
            </Box>
            <Box color="gray.500" fontSize="md" mb={8}>
              {t('emailbanner')}
            </Box>

            <Box display="flex" alignItems="center" mb="4" maxW="420px">
              <FormLabel
                htmlFor="emailNotificationsEnabled"
                mb="0"
                fontWeight="600"
                fontSize="16px"
              >
                {t('emailNotify')}
              </FormLabel>
              <Switch
                id="emailNotificationsEnabled"
                isChecked={emailNotificationsEnabled}
                onChange={(e) => {
                  const newStatus = e.target.checked
                    ? NotificationStatus.Enabled
                    : NotificationStatus.Disabled;
                  handleStatusUpdate(newStatus);
                }}
                colorScheme="blue"
                ml={3}
              />
            </Box>
            <Box borderBottom="1px solid" borderColor="gray.50" mb="8" pb="2" />
          </FormControl>
        )}
        <FormControl>
          <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
            {t('customerDetails')}
          </Heading>

          <FormControl isInvalid={Boolean(errors.name)} maxWidth="420" mb="8">
            <FormLabel marginRight="0">{t('customerName')}</FormLabel>
            <Input ref={nameInputRef} onChange={handleCustomerNameChange} {...nameInputProps} />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.handle)} maxWidth="420">
            <FormLabel marginRight="0">{t('customerHandle')}</FormLabel>
            <Input {...register('handle')} />
            <FormErrorMessage>{errors.handle?.message}</FormErrorMessage>
          </FormControl>
        </FormControl>

        <Shield requiredPermissions={[Permission.FeatureCustomerScopedAccess]}>
          <FormControl>
            <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
              {t('users')}
            </Heading>
            {isNil(users) || isEmpty(users) ? (
              <>{t('noUsers')}</>
            ) : (
              <CustomerUsersTable table={table} isDisabled={!isSubscriptionsEnabled} />
            )}
          </FormControl>
        </Shield>

        <Shield requiredPermissions={[Permission.WaveSubscriptionUpdate]}>
          <FormControl>
            <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
              {t('subscriptions')}
            </Heading>
            <CustomerSubscriptions
              key={originalSubscriptionIds.join(',')} // ✅ remounts when server data changes
              subscriptions={subscriptions}
              currentSubscriptionIds={customerSubscriptionIds}
              displayCount={customer.displayCount}
              onChange={handleSubscriptionIdsChange}
            />
          </FormControl>
        </Shield>
      </VStack>
    </form>
  );
}

OrganizationCustomerEditPage.graphql = {
  mutations: {
    EditCustomerNew: gql`
      mutation EditCustomerNew($input: CustomerUpdateInputNew!) {
        customerUpdateNew(input: $input) {
          id
          handle
          name
          avatarUrl
          ...CustomerSubscriptionData_customer
        }
      }
    `,
  },
};
